import { useEffect, useState, useRef } from "react";
import FolderList from "../FolderList/folder-list.component";
import { $bulkSelectMode, $bulkSelectedConversations, $userFolders, $allBulkSelectableConversations } from "../../store";
import {
	Container,
	ConversationFiltersButton,
	FlexContainer,
	FolderListWrapper,
	ButtonsContainer,
	SelectAllRow,
	SelectButton,
} from "./styles";
import { useQuery, useMutation, useReactiveVar } from "@apollo/client";
import { MenuItem, MenuButton, MenuHeader, ControlledMenu } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import { getUserId } from "../../helpers/functions";
import SendTemplateMessage from "../SendTemplateMessage/send-template-message.component";
import {
	GET_INBOX_DATA,
	GET_AI_USERS,
	SEARCH_CONVERSATIONS_MULTISELECT,
	GET_ARCHIVE_FOLDER_BY_INBOX,
	BULK_MOVE_TO_ARCHIVE,
	GET_FOLDERS_IN_INBOX,
	BULK_RESTORE_FROM_ARCHIVE
} from "./query";
import { useToasts } from "react-toast-notifications";
import {
	$conversationListSearchTerm,
	$userInboxes,
	$conversationUserFilter,
	$conversationTagFilter,
	$conversationDepartmentFilter,
	$conversationContactFilter,
	$selectedInbox,
	$assignedToMeConversationFilter,
	$assignedToAIUserConversationFilter,
	$unassignedConversationFilter,
	$unreadConversationFilter,
	$unrepliedConversationFilter,
	$assignedToTeamsConversationFilter,
	$conversationInboxFilter,
} from "../../store";
import MsgBoxSelect from "../MsgBoxSelect/msg-box-select.component";
import client from "../../Apollo";
import { getInboxFilters, setInboxFilters } from "./persistent-filters-storage";
import { Tooltip } from "react-tooltip";

const tryParseFilter = (value) => {
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

const ConversationFilters = ({ rootStore }) => {
	const [isOpen, setOpen] = useState(false);
	const [archiveFolderSelected, setArchiveFolderSelected] = useState(false)
	const userInboxes = useReactiveVar($userInboxes);
	const selectedInbox = useReactiveVar($selectedInbox);
	const bulkSelectMode = useReactiveVar($bulkSelectMode);
	const bulkSelectedConversations = useReactiveVar($bulkSelectedConversations);
	const allBulkSelectableConversations = useReactiveVar($allBulkSelectableConversations);
	const userFolders = useReactiveVar($userFolders);
	const [isArchiving, setIsArchiving] = useState(false);
	const [isRestoring, setIsRestoring] = useState(false);
	const { addToast } = useToasts();

	const [bulkMoveToArchive] = useMutation(BULK_MOVE_TO_ARCHIVE);
	const [bulkRestoreFromArchive] = useMutation(BULK_RESTORE_FROM_ARCHIVE);

	const handleSelectAll = () => {
		$bulkSelectedConversations(["__all__"]);
	};

	const handleDeselectAll = () => {
		$bulkSelectedConversations([]);
	};

	const enterSelectMode = () => {
		$bulkSelectMode(true);
		$bulkSelectedConversations([]);
	};

	const exitSelectMode = () => {
		$bulkSelectMode(false);
		$bulkSelectedConversations([]);
	};

	const handleBulkArchive = async () => {
		const idsToArchive = bulkSelectedConversations.includes("__all__")
			? allBulkSelectableConversations
			: bulkSelectedConversations;

		if (!idsToArchive || idsToArchive.length === 0) {
			addToast("No conversations selected.", { appearance: "warning", autoDismiss: true });
			return;
		}

		try {
			setIsArchiving(true);
			const { data } = await client.query({
				query: GET_ARCHIVE_FOLDER_BY_INBOX,
				variables: { inboxId: selectedInbox },
				fetchPolicy: "network-only",
			});
			const archiveId = data?.msgbox_Folder?.[0]?.FolderId;
			if (!archiveId) {
				addToast("Could not find archive folder.", { appearance: "error", autoDismiss: true });
				return;
			}
			await bulkMoveToArchive({ variables: { conversationIds: idsToArchive, archiveId } });
			addToast(`${idsToArchive.length} conversation${idsToArchive.length > 1 ? "s" : ""} archived.`, {
				appearance: "success",
				autoDismiss: true,
			});
			$bulkSelectMode(false);
			$bulkSelectedConversations([]);
		} catch (e) {
			addToast("Something went wrong archiving conversations.", { appearance: "error", autoDismiss: true });
		} finally {
			setIsArchiving(false);
		}
	};

	const handleBulkRestore = async () => {
		const idsToRestore = bulkSelectedConversations.includes("__all__")
			? allBulkSelectableConversations
			: bulkSelectedConversations;

		if (!idsToRestore || idsToRestore.length === 0) {
			addToast("No conversations selected.", { appearance: "warning", autoDismiss: true });
			return;
		}

		try {
			setIsRestoring(true);

			//replace folder selection logic
			const { data } = await client.query({
				query: GET_FOLDERS_IN_INBOX,
				variables: { inboxId: selectedInbox },
				fetchPolicy: "network-only",
			});
			//TODO: allow user to select from folders in the inbox, defaulting to first non-archive folder for now
			const folderId = data.msgbox_Folder.filter(folder => folder.IsArchive === false)[0].FolderId

			await bulkRestoreFromArchive({ variables: { conversationIds: idsToRestore, folderId } });
			addToast(`${idsToRestore.length} conversation${idsToRestore.length > 1 ? "s" : ""} restored.`, {
				appearance: "success",
				autoDismiss: true,
			});
			$bulkSelectMode(false);
			$bulkSelectedConversations([]);
		} catch (e) {
			addToast("Something went wrong restoring conversations.", { appearance: "error", autoDismiss: true });
		} finally {
			setIsRestoring(false);
		}
	};

	const [inboxFilters, setFilters] = useState(getInboxFilters());

	const [filterCount, setFilterCount] = useState(0);

	const [sendTemplateMessageIsOpen, setTemplateMessageModalOpen] =
		useState(false);
	const ref = useRef(null);
	const [menuItems, setMenuItems] = useState([]);
	const {
		loading,
		error,
		data: inboxData,
	} = useQuery(GET_INBOX_DATA, {
		variables: {
			userId: getUserId(),
		},
	});

	const { data: aiUserData } = useQuery(GET_AI_USERS);
	const aiUsers = aiUserData?.msgbox_User || [];

	useEffect(() => {
		if (inboxData) {
			let result = [];
			inboxData.msgbox_User[0].UserInboxes.forEach((inboxItem) => {
				if (!inboxItem.Inbox) return;
				let inboxEntry = {
					label: `${inboxItem.Inbox.Name} - Inbox`,
					value: {
						inbox: inboxItem.Inbox.InboxId,
						folders: inboxItem.Inbox.Folders,
						departments: [],
					},
				};
				result.push(inboxEntry);

				let departments =
					inboxData.msgbox_User[0].UserDepartments.flatMap(
						(deptItem) => {
							return deptItem.Department?.DepartmentInboxes.filter(
								(i) =>
									i.Inbox &&
									i.Inbox.InboxId === inboxItem.Inbox.InboxId
							).map((i) => {
								let departmentEntry = {
									label: `  -  ${deptItem.Department.Name}`,
									value: {
										department:
											deptItem.Department.DepartmentId,
										inboxId: i.Inbox.InboxId,
									},
								};
								result.push(departmentEntry);
								return departmentEntry;
							});
						}
					);
				inboxEntry.value.departments = departments.map(
					(dept) => dept?.value?.department
				);
			});
			inboxData.msgbox_User[0].UserDepartments.forEach((deptItem) => {
				if (deptItem.Department?.DepartmentInboxes.length == 0) {
					result.push({
						label: `${deptItem.Department.Name} - Team`,
						value: {
							department: deptItem.Department.DepartmentId,
							inboxId: null,
						},
					});
				}
			});
			setMenuItems(result);
		}
	}, [inboxData]);

	useEffect(() => {
		const filters = getInboxFilters();
		setFilters(filters);
	}, [selectedInbox]);

	useEffect(() => {
		if (inboxFilters) {
			setFilterCount(inboxFilters.length);
			if (inboxFilters.includes("assignedToMe")) {
				const userId = getUserId();
				$assignedToMeConversationFilter(parseInt(userId));
			} else {
				$assignedToMeConversationFilter(null);
			}
			if (inboxFilters.includes("unassigned")) {
				$unassignedConversationFilter(true);
			} else {
				$unassignedConversationFilter(null);
			}
			if (inboxFilters.includes("unread")) {
				$unreadConversationFilter(true);
			} else {
				$unreadConversationFilter(null);
			}
			if (inboxFilters.includes("unreplied")) {
				$unrepliedConversationFilter(true);
			} else {
				$unrepliedConversationFilter(null);
			}

			const aiUserFilters = inboxFilters
				.map(tryParseFilter)
				.filter((x) => x?.aiUser)
				.map((x) => x.aiUser);
			if (aiUserFilters && aiUserFilters.length > 0) {
				$assignedToAIUserConversationFilter(aiUserFilters);
			} else {
				$assignedToAIUserConversationFilter(null);
			}

			const departmentFilters = inboxFilters
				.map(tryParseFilter)
				.filter((x) => x?.department);

			if (departmentFilters) {
				$assignedToTeamsConversationFilter(departmentFilters);
			} else {
				$assignedToTeamsConversationFilter(null);
			}

			const iFilters = inboxFilters
				.map(tryParseFilter)
				.filter((x) => x?.inbox);
			if (iFilters && iFilters.length > 0) {
				$conversationInboxFilter(iFilters);
			} else {
				$conversationInboxFilter([]);
			}

			[...iFilters, ...departmentFilters].filter((item) => {
				let inMenuItems = menuItems.filter((mi) => {
					return JSON.stringify(mi.value) === JSON.stringify(item);
				});

				if (inMenuItems.length == 0 && menuItems.length > 0) {
					setInboxFilters(JSON.stringify(item), false);
				}
			});

			let f = getInboxFilters();
			if (JSON.stringify(inboxFilters) !== JSON.stringify(f))
				setFilters(f);
		} else {
			setFilterCount(0);
			$assignedToMeConversationFilter(null);
			$assignedToAIUserConversationFilter(null);
			$unassignedConversationFilter(null);
			$conversationInboxFilter([]);
			$unreadConversationFilter(null);
			$unrepliedConversationFilter(null);
			$assignedToTeamsConversationFilter(null);
		}
	}, [inboxFilters, menuItems]);

	const handleFilterChange = (filter, stringify = false) => {
		setInboxFilters(
			stringify ? JSON.stringify(filter.value) : filter.value,
			filter.checked
		);
		let filters = getInboxFilters();
		if (
			filters &&
			filter.value.department &&
			filter.value.inboxId &&
			filter.checked
		) {
			let departmentsInbox = menuItems.find(
				(item) => item.value.inbox == filter.value.inboxId
			);
			if (!filters.includes(JSON.stringify(departmentsInbox.value))) {
				console.log(departmentsInbox);
				setInboxFilters(JSON.stringify(departmentsInbox.value), true);
			}
		}
		if (filters && filter.value.inbox && !filter.checked) {
			let deps = filters.filter((x) => tryParseFilter(x)?.department);
			deps.forEach((dep) => {
				if (tryParseFilter(dep)?.inboxId === filter.value.inbox) {
					setInboxFilters(dep, false);
				}
			});
		}
		filters = getInboxFilters();
		setFilters(filters);
	};

	const handleFilterClose = (requestClose) => {
		switch (requestClose.reason) {
			case "cancel":
			case "blur":
				setOpen(false);
				break;
			case "click":
				break;
			default:
				setOpen(false);
				break;
		}
	};

	const loadTagsOptions = (searchTerm) => {
		return new Promise(async (resolve, reject) => {
			const groups = [];

			if (searchTerm.length > 2) {
				groups.push({
					label: "Search for",
					options: [
						{
							label: searchTerm,
							value: { searchTerm: `%${searchTerm}%` },
						},
					],
				});

				const resp = await client.query({
					query: SEARCH_CONVERSATIONS_MULTISELECT,
					variables: {
						searchTerm: "%" + searchTerm + "%",
						userId: getUserId(),
						userInboxes: userInboxes,
					},
					fetchPolicy: "network-only",
				});

				const options = resp.data.msgbox_Tag.map((tag) => ({
					label: tag.Name,
					value: { tagId: tag.TagId },
				}));

				groups.push({
					label: "Tags",
					options: options,
				});

				const options1 = resp.data.msgbox_User.map((user) => ({
					label: user.FirstName + " " + user.LastName,
					value: { userId: user.UserId },
					color: "pink",
				}));
				groups.push({
					label: "Users",
					options: options1,
				});

				const options2 = resp.data.msgbox_Department.map(
					(department) => ({
						label: department.Name,
						value: { departmentId: department.DepartmentId },
						color: "pink",
					})
				);
				groups.push({
					label: "Departments",
					options: options2,
				});

				let arr = [];
				resp.data.msgbox_Contact.forEach((contact) => {
					let existingItem = arr.find(
						(item) => item.label == contact.Name
					);
					if (!existingItem) {
						arr.push({
							label: contact.Name,
							value: { contacts: [contact.ContactId] },
						});
					} else {
						existingItem.value.contacts.push(contact.ContactId);
					}
				});
				groups.push({
					label: "Contacts",
					options: arr,
				});
			}
			resolve(groups);
		});
	};

	const handleTagFilterChange = (values) => {
		const userFilter = values
			.filter((x) => x.value.userId)
			.map((x) => x.value.userId);
		const tagFilter = values
			.filter((x) => x.value.tagId)
			.map((x) => x.value.tagId);

		const departmentFilter = values
			.filter((x) => x.value.departmentId)
			.map((x) => x.value.departmentId);

		const contactFilter = values
			.filter((x) => x.value.contacts)
			.flatMap((x) => x.value.contacts);

		const searchFilter = values
			.filter((x) => x.value.searchTerm)
			.map((x) => x.value.searchTerm);

		$conversationListSearchTerm(searchFilter[0]);
		$conversationUserFilter(
			userFilter.length > 0
				? { User: { UserId: { _in: userFilter } } }
				: {}
		);
		$conversationTagFilter(
			tagFilter.length > 0
				? { TagContacts: { TagId: { _in: tagFilter } } }
				: {}
		);

		$conversationDepartmentFilter(
			departmentFilter.length > 0
				? {
						Department: {
							DepartmentId: {
								_in: departmentFilter,
							},
						},
				  }
				: {}
		);

		$conversationContactFilter(
			contactFilter.length > 0
				? {
						ContactId: {
							_in: contactFilter,
						},
				  }
				: {}
		);
	};

	if (loading) return null;

	if (error) return null;

	const selectionSuffix = bulkSelectedConversations.includes("__all__")
		? " (All)"
		: bulkSelectedConversations.length > 0
		? ` (${bulkSelectedConversations.length})`
		: "";

	return (
		<Container>
			<FlexContainer>
				<FolderListWrapper>
					<FolderList setArchiveFolderSelected={setArchiveFolderSelected}/>
				</FolderListWrapper>
				<ButtonsContainer>
					<div className="filter">
						<MenuButton
							styles={{ position: "relative" }}
							onClick={() => setOpen(true)}
							ref={ref}
							data-tooltip-id="filter-tooltip"
							data-tooltip-content="Filter conversations"
						>
							<i className="fas fa-filter"></i>
							{filterCount !== 0 ? (
								<div className="filter-count">
									{filterCount}
								</div>
							) : null}
						</MenuButton>
						<Tooltip id="filter-tooltip" place="top" />
						<ControlledMenu
							anchorRef={ref}
							isOpen={isOpen}
							onClose={(requestClose) =>
								handleFilterClose(requestClose)
							}
						>
							<MenuHeader>Status</MenuHeader>
							<MenuItem
								type="checkbox"
								value="unread"
								checked={
									inboxFilters &&
									inboxFilters.includes("unread")
								}
								onClick={handleFilterChange}
							>
								Unread
							</MenuItem>
							<MenuItem
								type="checkbox"
								value="unreplied" 
								checked={
									inboxFilters &&
									inboxFilters.includes("unreplied") 
								}
								onClick={handleFilterChange}
							>
								Unreplied
							</MenuItem>
							<MenuHeader>Assignment</MenuHeader>
							<MenuItem
								type="checkbox"
								value="assignedToMe"
								checked={
									inboxFilters &&
									inboxFilters.includes("assignedToMe")
								}
								onClick={handleFilterChange}
							>
								Assigned To Me
							</MenuItem>
							<MenuItem
								type="checkbox"
								value="unassigned"
								checked={
									inboxFilters &&
									inboxFilters.includes("unassigned")
								}
								onClick={handleFilterChange}
							>
								Unassigned
							</MenuItem>
							{aiUsers.length > 0 && (
								<MenuHeader>AI Users</MenuHeader>
							)}
							{aiUsers.map((aiUser) => {
								const value = { aiUser: aiUser.UserId };
								return (
									<MenuItem
										key={`ai-user-${aiUser.UserId}`}
										type="checkbox"
										value={value}
										checked={
											inboxFilters &&
											inboxFilters.includes(
												JSON.stringify(value)
											)
										}
										onClick={(f) => {
											handleFilterChange(f, true);
										}}
									>
										{aiUser.FirstName}
									</MenuItem>
								);
							})}
							{menuItems.length > 0 && (
								<MenuHeader>Inboxes &amp; Teams</MenuHeader>
							)}
							{menuItems.map((item) => {
								return (
									<MenuItem
										key={JSON.stringify(item.value)}
										type="checkbox"
										value={item.value}
										checked={
											inboxFilters &&
											inboxFilters.includes(
												JSON.stringify(item.value)
											)
										}
										onClick={(f) => {
											handleFilterChange(f, true);
										}}
									>
										{item.label}
									</MenuItem>
								);
							})}
						</ControlledMenu>
					</div>

					<SelectButton
						data-tooltip-id="bulk-select-tooltip"
						data-tooltip-content="Select conversations"
						onClick={enterSelectMode}
						className={bulkSelectMode ? "active" : ""}
					>
						<i className="fas fa-check-square"></i>
					</SelectButton>
					<Tooltip id="bulk-select-tooltip" place="top" />				</ButtonsContainer>

					<ConversationFiltersButton
						id="send-template-message"
						data-tooltip-id="send-template-message-tooltip"
						data-tooltip-content="Send a notification"
						onClick={() => {
							setTemplateMessageModalOpen(true);
						}}
						icon="far fa-edit"
					/>
					<Tooltip id="send-template-message-tooltip" place="top" />				
			</FlexContainer>		{bulkSelectMode ? (
					<SelectAllRow>
						<button
							className="cancel-btn"
							onClick={exitSelectMode}
							data-tooltip-id="bulk-cancel-tooltip"
							data-tooltip-content="Exit selection"
						>
							<i className="fas fa-times"></i>
						</button>
						<Tooltip id="bulk-cancel-tooltip" place="top" />
						<button
							className="select-all-btn"
							onClick={handleSelectAll}
						>
							Select all
						</button>
						{bulkSelectedConversations.length > 0 && (
							<button
								className="deselect-btn"
								onClick={handleDeselectAll}
							>
								Deselect all
							</button>
						)}
						<span className="selection-count">
							{bulkSelectedConversations.includes("__all__")
								? "All selected"
								: bulkSelectedConversations.length > 0
								? `${bulkSelectedConversations.length} selected`
								: "None selected"}
						</span>
						<button
							className="confirm-archive-btn"
							onClick={() =>
								archiveFolderSelected
									? handleBulkRestore()
									: handleBulkArchive()
							}
							disabled={
								bulkSelectedConversations.length === 0 ||
								isArchiving ||
								isRestoring
							}
						>
							{archiveFolderSelected
								? isRestoring
									? "Restoring..."
									: `Restore${selectionSuffix}`
								: isArchiving
								? "Archiving..."
								: `Archive${selectionSuffix}`}
						</button>
					</SelectAllRow>
				) : (
					<MsgBoxSelect
						key={1}
						onChange={(options) => {
							handleTagFilterChange(options);
						}}
						name="tags"
						hasLabel={false}
						loadOptions={loadTagsOptions}
						table
						async
						isMulti
						placeholder="Search..."
					/>
				)}
			<SendTemplateMessage
				rootStore={rootStore}
				isOpen={sendTemplateMessageIsOpen}
				close={() => {
					setTemplateMessageModalOpen(false);
				}}
			/>
		</Container>
	);
};

export default ConversationFilters;
